import { Alert } from '@creditornot/cb-alert';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import * as api from 'apis/merchant-onboarding-service/api';
import WaiterOrder from 'common/illustrations/WaiterOrder';
import { Typography as T } from 'common/typography';

import ContractLinks from './ContractLinks';
import { BREAKPOINT_MOBILE } from '../constants';

const Heading = styled(T.Heading1)`
  text-align: center;
`;

const Subheading = styled(T.Heading6)`
  text-align: center;
  font-weight: 400;
`;

const LinksContainer = styled(T.Body3)`
  max-width: 620px;
  width: 100%;
`;

const Footer = styled(T.Body3)``;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80%;
  margin: 0 auto;
  padding-inline: 16px;

  ${Heading} {
    margin-block-start: 32px;
    font-size: 48px;

    @media (max-width: ${BREAKPOINT_MOBILE}px) {
      font-size: 40px;
    }
  }

  ${Subheading} {
    margin-block-start: 16px;
    font-size: 20px;

    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      font-size: 20px;
    }
  }

  ${LinksContainer} {
    margin-block-start: 32px;
  }

  ${Footer} {
    margin-block-start: 16px;
    margin-block-end: 16px;
    font-size: 14px;
  }
`;

const SpinnerContainer = styled.div`
  height: 100%;
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  align-items: center;
`;

const ErrorAlert = styled(Alert).attrs({
  variant: 'error',
})`
  margin-block: 16px;
`;

const StyledIllustration = styled(WaiterOrder)`
  max-width: 320px;
`;

const Contracts = () => {
  const { data: userContracts, isError: loadingUserInformationFailed } = useQuery(['/me/contracts'], () =>
    api.getUserContractDetails(),
  );

  return (
    <Root>
      <StyledIllustration />
      <Heading>
        <FormattedMessage id="merchant-sign-up.contract-links.sign-your-contract" />
      </Heading>
      <Subheading>
        <FormattedMessage id="merchant-sign-up.contract-links.time-to-sign-partner-contract" />
      </Subheading>

      <LinksContainer>
        {loadingUserInformationFailed ? (
          <ErrorAlert data-test-id="MerchantSignUpFailedToFetchUserContracts">
            <FormattedMessage id="merchant-sign-up.errors.user-contracts-fetch-failed" />
          </ErrorAlert>
        ) : userContracts === undefined ? (
          <SpinnerContainer data-test-id="merchant-sign-up-spinner">
            <Spinner size="medium" />
          </SpinnerContainer>
        ) : userContracts.length === 0 ? (
          <Redirect to="/merchant-sign-up/thanks" />
        ) : (
          <ContractLinks userContracts={userContracts} />
        )}
      </LinksContainer>

      <Footer>
        <FormattedMessage id="merchant-sign-up.contract-links.we-sent-you-an-email" />
      </Footer>
    </Root>
  );
};

export default Contracts;
